import Api from '../axios/api';
import { ProductInterface } from '../interfaces/product.interface';

export interface UpdatedProduct {
	id: number;
	name: string;
	companyId: number;
	sourceProductId: number;
	photoUrl: string;
	pricePerUnit: number;
	location: {
		type: string;
		coordinates: number[];
	};
	availableQuantity: number;
	minOrderQuantity: number;
	createdAt: Date;
	markup: number;
	type?: string;
	size?: string;
	productBaseId?: number;
}

interface PostProductResponse {
	data: UpdatedProduct;
}

export interface CreateProductParams {
	photoUrl?: string;
	pricePerUnit: number;
	availableQuantity: number;
	minOrderQuantity: number;
	sourceProductId: number;
	lat?: number;
	long?: number;
	unlimited: boolean;
	countryCode?: string;
	address?: string;
	productBaseId?: number;
}

interface Translation {
	language: string;
	name: string;
}

export interface DefaultProductParams {
	name: string;
	categoryId: string;
	photoUrl?: string;
	countryCode: string;
	size: string;
	type: string;
	unit: string;
	translations: Translation[];
}

export interface RequestProductsParams {
	status: string;
	categoryId?: string | undefined;
	limit: number;
	offset?: number;
	role: string;
	coordinates?: number[];
	productBaseId?: number;
	search?: string;
}

export interface RequestProductsAdminParams {
	verifiedBy?: string;
	limit: number;
	offset?: number;
	status?: string;
	companyId?: number;
	sortDirection?: string;
	sortBy?: string;
	range?: number;
	coordinates?: number[];
	ids?: number[];
	categoryId?: string;
	language?: string;
	search?: string;
	countryCode?: string;
	productBaseId?: number;
}

export interface ProductStoresParams {
	limit: number;
	offset?: number;
	ids?: number[];
	companyIds?: number[];
}

export interface ProductStoresResponse {
	data: {
		count: number;
		items: ProductStoreInterface[];
	};
}

export interface ProductStoreInterface {
	id: number;
	createdBy: string;
	companyId: number;
	address: string;
	name: string;
	countryCode: string;
	location: {
		type: string;
		coordinates: number[];
	};
	createdAt: string;
	updatedAt: string;
	workingHours?: Record<string, any>;
	workingDays?: Record<string, any>;
	productCount?: number;
}

export interface CreateStoresParams {
	name: string;
	address: string;
	lat: number;
	long: number;
	countryCode: string;
	workingHours?: Record<string, any>;
	workingDays?: Record<string, any>;
}

export interface ProductResponse {
	count: number;
	items: ProductInterface[];
}

export interface ResponseProductListResponse {
	data: ProductResponse;
}

export interface EditProduct {
	vat?: number;
	markup?: number;
	availableQuantity?: number;
	pricePerUnit?: number;
	name?: string;
	address?: string;
	countryCode?: string;
	type?: string;
	lat?: number;
	long?: number;
}

export interface EditMeProduct {
	availableQuantity?: number;
	pricePerUnit?: number;
	unlimited?: boolean;
	minOrderQuantity?: number;
	maxOrderCapacity?: number;
}

export interface ProductEditResponse {
	data: ProductInterface;
}

interface DeleteProductResponse {
	response: string;
}

interface QueryParams {
	limit: number;
	offset: number | undefined;
	language?: string;
	search?: string;
	categoryId?: string;
	ids?: string[];
	coordinates?: number[];
	range?: number;
	sortBy?: string;
	sortDirection?: string;
	verifiedBy?: string;
	status?: string;
	companyId?: number;
	countryCode?: string;
	productBaseId?: number;
}

export class ProductService {
	static createProduct(
		body: CreateProductParams,
	): Promise<PostProductResponse> {
		return Api.post(`/v1/producer/products`, {
			...body,
		});
	}

	static editMeProduct(
		id: number,
		body: EditMeProduct,
	): Promise<ProductEditResponse> {
		return Api.patch(`/v1/producer/products/${id}`, {
			...body,
		});
	}

	static getList(
		query: RequestProductsParams,
	): Promise<ResponseProductListResponse> {
		const {
			limit,
			offset,
			categoryId,
			coordinates,
			role,
			status,
			productBaseId,
			search,
		} = query;

		const params: QueryParams = {
			limit,
			offset,
			language: localStorage.getItem('lang') || 'pl',
			...(status && status !== 'all' && { status }),
			...(categoryId && { categoryId }),
			...(coordinates?.length && { coordinates, range: 500 }),
			...(productBaseId && { productBaseId }),
			...(search?.length && { search }),
		};

		return Api.get(`/v1/${role}/products`, { params });
	}

	static getListAdmin(
		query: RequestProductsAdminParams,
	): Promise<ResponseProductListResponse> {
		const {
			limit,
			offset,
			status,
			search,
			verifiedBy,
			countryCode,
			sortDirection,
			sortBy,
			companyId,
			productBaseId,
		} = query;

		const params: RequestProductsAdminParams = {
			limit,
			offset,
			language: localStorage.getItem('lang') || 'pl',
			...(status && status !== 'all' && { status }),
			...(search?.length && { search }),
			...(countryCode && countryCode !== 'all' && { countryCode }),
			...(sortDirection && { sortDirection }),
			...(sortBy && { sortBy }),
			...(verifiedBy && { verifiedBy }),
			...(typeof companyId === 'number' && companyId && { companyId }),
			...(typeof productBaseId === 'number' &&
				productBaseId && { productBaseId }),
		};

		return Api.get('/v1/admin/products', { params });
	}

	static getProductDatabase(
		query: RequestProductsAdminParams,
	): Promise<ResponseProductListResponse> {
		const {
			limit,
			offset,
			search,
			categoryId,
			sortBy,
			sortDirection,
			verifiedBy,
			countryCode,
		} = query;
		let params: RequestProductsAdminParams = {
			limit,
			offset,
			language: localStorage.getItem('lang') || 'pl',
			sortBy,
			sortDirection,
			verifiedBy,
			countryCode,
		};
		if (search?.length) {
			params = {
				...params,
				search,
			};
		}
		if (categoryId?.length) {
			params = {
				...params,
				categoryId,
			};
		}
		if (countryCode === 'all') {
			delete params.countryCode;
		}

		return Api.get('/v1/admin/products/templates', { params });
	}

	static actionProduct(
		id: number,
		action: string,
	): Promise<PostProductResponse> {
		return Api.post(`/v1/admin/products/${id}/${action}`);
	}

	static editProduct(
		id: number,
		body: EditProduct,
	): Promise<PostProductResponse> {
		return Api.patch(`/v1/admin/products/${id}`, {
			...body,
		});
	}

	static createProductAdmin(
		body: DefaultProductParams,
	): Promise<PostProductResponse> {
		return Api.post('/v1/admin/products/templates', {
			...body,
		});
	}

	static updateProductDatabase(
		id: number,
		body: DefaultProductParams,
	): Promise<ProductEditResponse> {
		return Api.patch(`/v1/admin/products/templates/${id}`, {
			...body,
		});
	}

	static deleteProductDatabase(id: number): Promise<DeleteProductResponse> {
		return Api.delete(`/v1/admin/products/templates/${id}`);
	}

	static getProductStores(
		query: ProductStoresParams,
		role: string = 'producer',
	): Promise<ProductStoresResponse> {
		return Api.get(`/v1/${role}/product-store`, {
			params: query,
		});
	}

	static createProductStores(
		body: CreateStoresParams,
	): Promise<ProductStoreInterface> {
		return Api.post('/v1/producer/product-store', {
			...body,
		});
	}

	static editProductStores(
		id: number,
		body: CreateStoresParams,
	): Promise<void> {
		return Api.patch(`/v1/producer/product-store/${id}`, {
			...body,
		});
	}
}
